/**
 * revShareTemplate.js
 *
 * Fills the [ADVISOR NAME], [ADDRESS], [EMAIL], and [EFFECTIVE DATE] placeholders
 * in the raw rev-share agreement text fetched from S3, using the manager's firm data
 * already present in the auth context (user.firm.*).
 *
 * The HFR side of the document is left untouched (HFR details are already hard-coded
 * in the master PDF; this only substitutes the Advisor side).
 */

/**
 * Build a formatted address string from individual firm fields.
 * Pass an already-resolved country display name via `countryDisplay` to override
 * the raw lookup value stored in `firm_country`.
 *
 * @param {object} firm          - user.firm from AuthContext (or registration form shape)
 * @param {string} [countryDisplay] - Optional resolved country display name (e.g. "Hong Kong").
 *                                    When omitted the raw firm_country value is used as-is.
 * @returns {string}
 */
export function buildFirmAddress(firm = {}, countryDisplay) {
  return [
    firm.firm_address,
    firm.firm_city,
    firm.firm_postal_code,
    countryDisplay !== undefined ? countryDisplay : firm.firm_country,
  ]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Derive a firmData object from user.firm (auth context shape).
 *
 * Pass resolved display values for country and job title so the template utility
 * stays decoupled from lookupService — callers (PostLoginAgreementModal,
 * RegistrationBillingStep) resolve lookups before calling this function.
 *
 * @param {object} user             - full user object from AuthContext
 * @param {string} [countryDisplay] - Resolved display name for firm_country lookup value
 * @returns {{ firmName: string, firmAddress: string, firmEmail: string }}
 */
export function buildFirmData(user = {}, countryDisplay) {
  const firm = user?.firm || {};
  return {
    firmName: firm.firm_name || '',
    firmAddress: buildFirmAddress(firm, countryDisplay),
    // Priority: firm_email → firm_contact_email → manager's own login email
    firmEmail: firm.firm_email || firm.firm_contact_email || user.email || '',
  };
}

/**
 * Replace all Advisor-side placeholders in the raw agreement text.
 *
 * Placeholders matched (case-insensitive where noted):
 *   [ADVISOR NAME]     → firmData.firmName
 *   [ADDRESS]          → firmData.firmAddress
 *   [EMAIL]            → firmData.firmEmail
 *   [EFFECTIVE DATE]   → today's date (long format)
 *
 * If firmData values are empty the placeholder is left in brackets so the
 * document remains clearly un-filled rather than showing a blank.
 *
 * @param {string} rawText   - Agreement text as extracted from the master PDF
 * @param {object} firmData  - { firmName, firmAddress, firmEmail }
 * @returns {string}
 */
export function fillRevShareTemplate(rawText = '', firmData = {}) {
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return rawText
    .replace(/\[ADVISOR NAME\]/g, firmData.firmName || '[ADVISOR NAME]')
    .replace(/\[ADDRESS\]/g, firmData.firmAddress || '[ADDRESS]')
    .replace(/\[EMAIL\]/g, firmData.firmEmail || '[EMAIL]')
    .replace(/\[EFFECTIVE DATE\]/gi, today);
}
